import { defineComponent as _defineComponent } from 'vue'
import { offset, flip, size } from '@floating-ui/vue'
import { computed, nextTick, ref, watch } from 'vue'

import DocumentListItem from './DocumentListItem.vue'
import DropZone from '@/components/core/DropZone.vue'
import CommandInput from '@/components/core/command/CommandInput.vue'
import CommandList from '@/components/core/command/CommandList.vue'
import CommandEmpty from '@/components/core/command/CommandEmpty.vue'
import CommandGroup from '@/components/core/command/CommandGroup.vue'
import CommandItem from '@/components/core/command/CommandItem.vue'
import LCommand from '@/components/core/command/Command.vue'
import { unwrapElement, useDismiss, usePopover } from '@/composables/popover'
import { documentModule } from '@/store'
import { Document } from '@/types/document'
import { DocumentType } from '@/utils/enums'

export interface DocumentCommandProps {
  /**
   * The supported file types that can be uploaded.
   *
   * @example ['image/*', 'application/pdf']
   */
  supportedFileTypes: string[]
  /**
   * The minWidth for the floating element, if it should not match the reference ref width.
   * Number in pixels.
   */
  minWidth?: number
  /**
   * A function to filter the documents that are shown in the command list.
   */
  filter?: (value: Document, index?: number, array?: Document[]) => boolean
}

export default /*#__PURE__*/_defineComponent({
  __name: 'DocumentCommand',
  props: {
    supportedFileTypes: null,
    minWidth: null,
    filter: null
  },
  emits: ["input", "upload", "link"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void
  (e: 'upload', event: Event): void
  (e: 'link', id: Document): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /**
   * The supported file types that can be uploaded.
   *
   * @example ['image/*', 'application/pdf']
   */
  supportedFileTypes: string[]
  /**
   * The minWidth for the floating element, if it should not match the reference ref width.
   * Number in pixels.
   */
  minWidth?: number
  /**
   * A function to filter the documents that are shown in the command list.
   */
  filter?: (value: Document, index?: number, array?: Document[]) => boolean
};





const searchInput = ref('')

const files = computed(() => {
  const files = documentModule.files.filter((file) => {
    const isFile = file.type === DocumentType.FILE
    if (!isFile) {
      return false
    }
    if (props.filter) {
      return props.filter(file)
    }
    return true
  })

  return files
})

// Show popover Functionality --------------------------------------------------

const showCommand = ref(false)
const isOpen = computed({
  get: () => showCommand.value,
  set: (value: boolean) => {
    showCommand.value = value
    emit('input', value)
  },
})

watch(isOpen, (newIsOpen) => {
  if (!newIsOpen) {
    nextTick(() => {
      const referenceElement = unwrapElement(referenceRef.value)
      if (!(referenceElement instanceof HTMLElement)) {
        return
      }
      // focus the trigger element directly or the reference element div
      const elementToFocus = referenceElement.firstChild ?? referenceElement
      if (elementToFocus instanceof HTMLElement) {
        elementToFocus.focus()
      }
    })
  }
})

const { referenceRef, floatingRef, floatingStyles } = usePopover({
  placement: 'bottom-end',
  isOpen,
  middleware: [
    offset(4),
    flip({ padding: 16, fallbackPlacements: ['top-end'] }),
    size({
      padding: 16,
      apply({ availableHeight, availableWidth, elements, rects }) {
        const minWidthValue = props.minWidth ?? rects.reference.width

        Object.assign(elements.floating.style, {
          minWidth: `${minWidthValue}px`,
          maxWidth: `${Math.min(minWidthValue, availableWidth)}px`,

          maxHeight: files.value.length
            ? `${Math.min(384, Math.max(320, availableHeight))}px`
            : '256px',
        })
      },
    }),
  ],
})
useDismiss({ referenceRef, floatingRef, isOpen })

// Handlers --------------------------------------------------------------------

function onInputChange(event: Event) {
  emit('upload', event)
  isOpen.value = false
}

function onDrop(_files: File[] | null, event: Event) {
  emit('upload', event)
  isOpen.value = false
}

function onSelect(file: Document) {
  emit('link', file)
  isOpen.value = false
}

return { __sfc: true,props, emit, searchInput, files, showCommand, isOpen, referenceRef, floatingRef, floatingStyles, onInputChange, onDrop, onSelect, DocumentListItem, DropZone, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, LCommand }
}

})